import React from 'react'
import { Box, BoxProps } from '@mui/material'
import { styled } from '@mui/material/styles'

export type StepProgressTone = 'ink' | 'spectrum'

export interface StepProgressProps extends BoxProps {
  /** Zero-based index of the furthest completed/active step */
  current: number
  /** Total steps (segments) */
  total: number
  labels?: string[]
  /** `spectrum` = gradient done segments (create flow); `ink` = default */
  tone?: StepProgressTone
  /** Fixed segment width in px; omit for equal flex fill */
  segmentWidth?: number
}

const Track = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
})

const Segment = styled(Box, {
  shouldForwardProp: (p) => p !== '$done' && p !== '$tone' && p !== '$width',
})<{ $done?: boolean; $tone: StepProgressTone; $width?: number }>(({ $done, $tone, $width }) => ({
  width: $width ?? undefined,
  flex: $width ? '0 0 auto' : 1,
  height: 3,
  borderRadius: 3,
  background: $done
    ? $tone === 'spectrum'
      ? 'var(--ds-gradient-spectrum, var(--ds-ink))'
      : 'var(--ds-ink)'
    : 'var(--ds-border)',
  transition: 'background var(--ds-duration-normal) var(--ds-ease)',
}))

const Caption = styled('span')({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 10,
  letterSpacing: '0.1em',
  color: 'var(--ds-ink-subtle)',
  whiteSpace: 'nowrap',
})

/** Quiet multi-step progress — segment bars, optional mono caption. */
export function StepProgress({
  current,
  total,
  labels,
  tone = 'ink',
  segmentWidth,
  ...rest
}: StepProgressProps) {
  const count = Math.max(1, total)
  const doneThrough = Math.min(Math.max(0, current), count - 1)
  const caption = labels?.length ? labels.join(' → ') : undefined

  return (
    <Track
      role='progressbar'
      aria-valuenow={doneThrough + 1}
      aria-valuemin={1}
      aria-valuemax={count}
      aria-label={caption ? `Steps: ${caption}` : `Step ${doneThrough + 1} of ${count}`}
      {...rest}
    >
      {Array.from({ length: count }).map((_, i) => (
        <Segment
          key={i}
          $done={i <= doneThrough}
          $tone={tone}
          $width={segmentWidth}
          title={labels?.[i]}
        />
      ))}
      {caption && <Caption>{caption}</Caption>}
    </Track>
  )
}
